import React, { useState, useEffect } from 'react';
import { FileOutput, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const ExtractPages = () => {
    const [file, setFile] = useState(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [pagesToExtract, setPagesToExtract] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');
    const [showPreview, setShowPreview] = useState(false);

    // Step 1: Upload PDF
    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPdfDoc(pdf);
                setTotalPages(pdf.getPageCount());
                setPagesToExtract([]);
                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    // Step 2: Select pages to extract
    const togglePageSelection = (pageNum) => {
        setPagesToExtract(prev => {
            if (prev.includes(pageNum)) {
                return prev.filter(p => p !== pageNum);
            } else {
                return [...prev, pageNum].sort((a, b) => a - b);
            }
        });
    };

    const selectAllPages = () => {
        setPagesToExtract(Array.from({ length: totalPages }, (_, i) => i + 1));
    };

    const deselectAllPages = () => {
        setPagesToExtract([]);
    };

    // Step 3 & 4: Create new PDF and Download extracted pages
    const extractSelectedPages = async () => {
        if (pagesToExtract.length === 0) {
            toast.warning('Please select at least one page to extract');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(10);

            // Step 3: Create a new PDF document
            const newPdf = await PDFDocument.create();
            setProgress(30);

            // Convert page numbers to 0-indexed array
            const pageIndices = pagesToExtract.map(p => p - 1);

            setProgress(50);

            // Copy the selected pages
            const copiedPages = await newPdf.copyPages(pdfDoc, pageIndices);
            copiedPages.forEach(page => newPdf.addPage(page));

            setProgress(80);

            // Save the PDF
            const pdfBytes = await newPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            // Set preview
            const timestamp = new Date().toISOString().slice(0, 10);
            const filename = `extracted-pages-${timestamp}.pdf`;
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success(`${pagesToExtract.length} page(s) extracted! Opening preview...`);

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Extract error:', error);
            toast.error(`Failed to extract pages: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    // Generate live preview when pages are selected
    const generateLivePreview = async () => {
        if (!pdfDoc || pagesToExtract.length === 0) {
            setPreviewBlob(null);
            return;
        }

        try {
            const newPdf = await PDFDocument.create();
            const pageIndices = pagesToExtract.map(p => p - 1);
            const copiedPages = await newPdf.copyPages(pdfDoc, pageIndices);
            copiedPages.forEach(page => newPdf.addPage(page));

            const pdfBytes = await newPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const timestamp = new Date().toISOString().slice(0, 10);
            const filename = `extracted-pages-${timestamp}.pdf`;
            
            setPreviewBlob(blob);
            setPreviewFilename(filename);
        } catch (error) {
            console.error('Preview generation error:', error);
            setPreviewBlob(null);
        }
    };

    // Auto-update preview when selection changes
    useEffect(() => {
        if (file && pdfDoc) {
            const timeoutId = setTimeout(() => {
                generateLivePreview();
            }, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [pagesToExtract, file, pdfDoc]);

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    // Add new file or create new PDF
                    toast.info('Add new file functionality');
                }}
                notificationCount={pagesToExtract.length}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <FileOutput size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Extract Pages
                </h1>
                <p className="tool-description">
                    Extract specific pages from your PDF and create a new document. Select the pages you want to keep.
                </p>
            </div>

            {/* Step 1: File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <>
                    <Alert variant="success" title="PDF Loaded">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                                <FileText size={16} />
                                <span>{file.name}</span>
                                <Badge variant="info">{totalPages} pages</Badge>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setFile(null);
                                    setPdfDoc(null);
                                    setTotalPages(0);
                                    setPagesToExtract([]);
                                }}
                            >
                                Change PDF
                            </Button>
                        </div>
                    </Alert>

                    {/* Step 2: Select pages to extract */}
                    <Card>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                <h3 style={{
                                    fontSize: 'var(--text-xl)',
                                    fontWeight: 'var(--font-bold)'
                                }}>
                                    Select Pages to Extract
                                </h3>
                                <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                                    <Button variant="ghost" size="sm" onClick={selectAllPages}>
                                        Select All
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={deselectAllPages}>
                                        Deselect All
                                    </Button>
                                </div>
                            </div>

                            <Alert variant="info">
                                <strong>Tip:</strong> Click on pages to select them for extraction. Selected pages will be saved to a new PDF.
                            </Alert>

                            {/* Page Thumbnails Grid */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                gap: 'var(--spacing-4)'
                            }}>
                                {Array.from({ length: totalPages }, (_, i) => {
                                    const pageNum = i + 1;
                                    const isSelected = pagesToExtract.includes(pageNum);

                                    return (
                                        <div
                                            key={pageNum}
                                            onClick={() => togglePageSelection(pageNum)}
                                            style={{
                                                cursor: 'pointer',
                                                transition: 'all var(--transition-base)'
                                            }}
                                        >
                                            <Card
                                                variant={isSelected ? 'elevated' : 'outlined'}
                                                style={{
                                                    border: isSelected
                                                        ? '2px solid var(--color-success)'
                                                        : '1px solid var(--color-border)',
                                                    backgroundColor: isSelected
                                                        ? 'rgba(34, 197, 94, 0.1)'
                                                        : 'var(--color-surface)',
                                                    position: 'relative'
                                                }}
                                            >
                                                {/* Page Thumbnail Placeholder */}
                                                <div style={{
                                                    aspectRatio: '8.5/11',
                                                    backgroundColor: 'var(--color-surface-elevated)',
                                                    borderRadius: 'var(--radius-md)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginBottom: 'var(--spacing-2)',
                                                    position: 'relative',
                                                    overflow: 'hidden'
                                                }}>
                                                    {/* Page Number */}
                                                    <div style={{
                                                        fontSize: 'var(--text-3xl)',
                                                        fontWeight: 'var(--font-bold)',
                                                        color: isSelected ? 'var(--color-success)' : 'var(--color-text-tertiary)'
                                                    }}>
                                                        {pageNum}
                                                    </div>

                                                    {/* Selection Indicator */}
                                                    {isSelected && (
                                                        <>
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                <FileOutput size={32} color="var(--color-success)" />
                                                            </div>
                                                            <Badge
                                                                variant="success"
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 'var(--spacing-2)',
                                                                    right: 'var(--spacing-2)'
                                                                }}
                                                            >
                                                                ✓
                                                            </Badge>
                                                        </>
                                                    )}
                                                </div>

                                                {/* Page Label */}
                                                <div style={{
                                                    textAlign: 'center',
                                                    fontSize: 'var(--text-sm)',
                                                    fontWeight: 'var(--font-medium)',
                                                    color: isSelected ? 'var(--color-success)' : 'var(--color-text-primary)'
                                                }}>
                                                    Page {pageNum}
                                                </div>
                                            </Card>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Selection Summary */}
                            {pagesToExtract.length > 0 && (
                                <Alert variant="success" style={{ marginTop: 'var(--spacing-4)' }}>
                                    <strong>{pagesToExtract.length} page{pagesToExtract.length !== 1 ? 's' : ''} selected for extraction</strong>
                                    <div style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--text-sm)' }}>
                                        Pages to extract: {pagesToExtract.join(', ')}
                                    </div>
                                    <div style={{ marginTop: 'var(--spacing-1)', fontSize: 'var(--text-sm)' }}>
                                        New PDF will contain {pagesToExtract.length} page{pagesToExtract.length !== 1 ? 's' : ''}
                                    </div>
                                </Alert>
                            )}
                        </Card>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <ProgressBar value={progress} striped showLabel />
                    )}

                    {/* Step 3 & 4: Extract and Download Button */}
                    <div className="button-group" style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
                        {previewBlob && (
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={() => setShowPreview(true)}
                                style={{ flex: 1 }}
                            >
                                <FileText size={20} style={{ marginRight: 'var(--spacing-2)' }} />
                                View Preview
                            </Button>
                        )}
                        <Button
                            variant="success"
                            size="lg"
                            onClick={extractSelectedPages}
                            disabled={isProcessing || pagesToExtract.length === 0}
                            loading={isProcessing}
                            style={{ flex: previewBlob ? 1 : 'none', width: previewBlob ? 'auto' : '100%' }}
                            fullWidth={!previewBlob}
                        >
                            <Download size={20} />
                            Extract {pagesToExtract.length} Page{pagesToExtract.length !== 1 ? 's' : ''} & Download
                        </Button>
                        <Button
                            variant="ghost"
                            size="lg"
                            onClick={deselectAllPages}
                            disabled={isProcessing || pagesToExtract.length === 0}
                        >
                            Clear
                        </Button>
                    </div>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && showPreview && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setShowPreview(false)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                        }
                    }}
                />
            )}
        </div>
    );
};

export default ExtractPages;
